/**
 * MentionAutocomplete — controlled @-mention dropdown.
 *
 * Wraps a textarea or input. When the caret is inside an `@token` segment,
 * shows a dropdown of matching profiles. Selecting a profile inserts it as
 * `@FirstName` into the text and emits a `mentions` array tracking the
 * profile_id behind each mention. The chat surface uses the `mentions`
 * array to ground prompt context against actual Profile data.
 *
 * Per Feature #3 spec: drop-down sorted by recent interaction frequency.
 * Disambiguation by role/department for duplicate names.
 *
 * V1 first ship: same-row dropdown anchored below the input. V1.x: positions
 * the dropdown next to the caret + supports keyboard navigation arrow keys.
 */

import { useState, useEffect, useRef } from "react";

export default function MentionAutocomplete({
  value,
  onChange,
  mentions = [],
  onMentionsChange,
  placeholder,
  rows = 3,
  bearerToken,
}) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [tokenStart, setTokenStart] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  // Watch for an active @token under the caret
  useEffect(() => {
    if (!inputRef.current) return;
    const caret = inputRef.current.selectionStart;
    const before = value.slice(0, caret);
    const m = before.match(/@(\w*)$/);
    if (m) {
      setTokenStart(caret - m[0].length);
      setSearch(m[1]);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setTokenStart(null);
    }
  }, [value]);

  // Fetch results
  useEffect(() => {
    if (!showDropdown || !bearerToken) {
      setResults([]);
      return;
    }
    const ctrl = new AbortController();
    fetch(`/.netlify/functions/profile-mention-search?q=${encodeURIComponent(search || "")}`, {
      headers: { authorization: `Bearer ${bearerToken}` },
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((b) => { setResults(b.results || []); setActiveIndex(0); })
      .catch(() => {});
    return () => ctrl.abort();
  }, [search, showDropdown, bearerToken]);

  const acceptMention = (profile) => {
    if (tokenStart === null) return;
    const before = value.slice(0, tokenStart);
    const after = value.slice(inputRef.current.selectionStart);
    const replacement = `@${profile.display_name} `;
    const newValue = before + replacement + after;
    onChange(newValue);
    if (onMentionsChange) {
      onMentionsChange([
        ...mentions.filter((m) => m.start < tokenStart || m.start >= tokenStart + replacement.length),
        {
          profile_id: profile.internal_uuid,
          display_name: profile.display_name,
          start: tokenStart,
          end: tokenStart + replacement.length - 1,
        },
      ]);
    }
    setShowDropdown(false);
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const newCaret = tokenStart + replacement.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCaret, newCaret);
      }
    });
  };

  const onKeyDown = (e) => {
    if (!showDropdown || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); acceptMention(results[activeIndex]); }
    else if (e.key === "Escape") { setShowDropdown(false); }
  };

  return (
    <div style={{ position: "relative" }} data-component="MentionAutocomplete">
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={rows}
        style={{
          width: "100%",
          padding: "12px 14px",
          fontFamily: "var(--ppr-typography-font-family-ui)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-background-surface)",
          color: "var(--ppr-color-text-primary)",
          resize: "vertical",
          outline: "none",
        }}
      />
      {showDropdown && results.length === 0 && search.length >= 2 && (
        <CreateNewProfileRow
          name={search}
          bearerToken={bearerToken}
          onCreated={(profile) => acceptMention(profile)}
        />
      )}
      {showDropdown && results.length > 0 && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "var(--ppr-color-background-surface)",
            border: "1px solid var(--ppr-color-border-default)",
            zIndex: 100,
            maxHeight: 360,
            overflow: "auto",
          }}
        >
          {results.map((p, i) => (
            <button
              key={p.internal_uuid}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); acceptMention(p); }}
              onMouseEnter={() => setActiveIndex(i)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 14px",
                background: i === activeIndex
                  ? "var(--ppr-color-accent-navy-soft)"
                  : "var(--ppr-color-background-surface)",
                border: "none",
                borderBottom: i === results.length - 1
                  ? "none"
                  : "1px solid var(--ppr-color-border-default)",
                cursor: "pointer",
                fontFamily: "var(--ppr-typography-font-family-ui)",
              }}
            >
              <div style={{ fontWeight: 600, color: "var(--ppr-color-accent-navy)" }}>
                {p.display_name}
              </div>
              <div style={{ fontSize: "12px", color: "var(--ppr-color-text-muted)", marginTop: 2 }}>
                {p.needs_disambiguation && p.full_name ? `${p.full_name} · ` : ""}
                {p.role_title || ""}{p.department ? ` · ${p.department}` : ""}
                {p.mode === "Lite" ? " · Lite" : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateNewProfileRow({ name, bearerToken, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/profile-auto-create", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
        body: JSON.stringify({ display_name: name, source: "auto_mention" }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.message || body.error || "Failed to create");
        return;
      }
      onCreated({ internal_uuid: body.internal_uuid, display_name: body.display_name });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        marginTop: 4,
        background: "var(--ppr-color-background-surface)",
        border: "1px solid var(--ppr-color-border-default)",
        padding: "12px 14px",
        zIndex: 100,
      }}
    >
      <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "var(--ppr-typography-font-size-sm)", color: "var(--ppr-color-text-secondary)", marginBottom: 8 }}>
        No matches for <strong>"{name}"</strong>.
      </div>
      <button
        onMouseDown={(e) => { e.preventDefault(); create(); }}
        disabled={busy}
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          padding: "8px 14px",
          border: "1px solid var(--ppr-color-border-default)",
          background: busy ? "var(--ppr-color-background-muted)" : "var(--ppr-color-accent-navy)",
          color: "var(--ppr-color-text-inverse)",
          cursor: busy ? "wait" : "pointer",
        }}
      >
        {busy ? "Creating…" : `+ Add ${name} as new Profile`}
      </button>
      {error && (
        <div style={{ marginTop: 8, color: "var(--ppr-color-accent-coral-dark)", fontSize: "12px" }}>
          {error}
        </div>
      )}
    </div>
  );
}
